import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Download, Megaphone, Repeat, Users } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "pending", label: "Pending" },
  { key: "failed", label: "Failed" },
  { key: "expired", label: "Canceled" },
];

const TYPE_OPTIONS = [
  { key: "all", label: "All types" },
  { key: "ad", label: "Ads" },
  { key: "subscription", label: "Subscriptions" },
];

const typeBadge = (t) => (t === "subscription" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#CC5A3A]/15 text-[#CC5A3A]");
const typeLabel = (t) => (t === "subscription" ? "Subscription" : "Ad");

const badge = (status) => {
  if (status === "paid") return "bg-[#2D6A4F]/15 text-[#2D6A4F]";
  if (status === "failed") return "bg-[#E63946]/15 text-[#E63946]";
  if (status === "initiated" || status === "open") return "bg-[#CC5A3A]/15 text-[#CC5A3A]";
  return "bg-[#1E3F33]/10 text-[#5C6B64]";
};

const label = (status) => (status === "paid" ? "Paid" : status === "failed" ? "Failed" : status === "expired" ? "Canceled" : "Pending");

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [activeSubs, setActiveSubs] = useState(null);

  useEffect(() => {
    api.get("/admin/payments")
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
    api.get("/admin/collectors", { params: { status: "active", page_size: 1 } })
      .then(({ data }) => setActiveSubs(data.total))
      .catch(() => setActiveSubs(null));
  }, []);

  const inRange = (iso) => {
    if (!range.start && !range.end) return true;
    if (!iso) return false;
    const d = iso.slice(0, 10);
    if (range.start && d < range.start) return false;
    if (range.end && d > range.end) return false;
    return true;
  };

  const filtered = useMemo(() => {
    return items.filter((t) => {
      if (type !== "all" && (t.type || "ad") !== type) return false;
      if (!inRange(t.created_at)) return false;
      if (status === "paid" && t.payment_status !== "paid") return false;
      if (status === "expired" && t.payment_status !== "expired") return false;
      if (status === "failed" && t.payment_status !== "failed") return false;
      if (status === "pending" && ["paid", "expired", "failed"].includes(t.payment_status)) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, range, status, type]);

  const totalPaid = filtered.filter((t) => t.payment_status === "paid").reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const adRevenue = filtered.filter((t) => t.payment_status === "paid" && (t.type || "ad") === "ad").reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const subRevenue = filtered.filter((t) => t.payment_status === "paid" && t.type === "subscription").reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const exportCSV = () => {
    if (!filtered.length) { toast.error("Nothing to export"); return; }
    const rows = filtered.map((t) => ({
      type: typeLabel(t.type),
      item: t.item || t.ad_title || "",
      payer: t.payer || t.advertiser_email || "",
      date: fmt(t.created_at),
      amount: Number(t.amount || 0).toFixed(2),
      currency: (t.currency || "usd").toUpperCase(),
      status: label(t.payment_status),
    }));
    downloadCSV(`payments-${type}-${status}.csv`, rows);
    toast.success("CSV downloaded");
  };

  return (
    <div className="container-x py-12" data-testid="admin-payments">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#2D6A4F]" /> Payments ({filtered.length})
          </h1>
          <p className="text-[#5C6B64] mt-2">Payment records for sponsored ad placements and collector Pro subscriptions.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card-soft" data-testid="admin-payments-summary-total">
          <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide"><CreditCard className="w-4 h-4 text-[#2D6A4F]" /> Total collected</div>
          <div className="font-heading text-2xl text-[#1E3F33] mt-2" data-testid="admin-payments-total">${totalPaid.toFixed(2)}</div>
        </div>
        <div className="card-soft" data-testid="admin-payments-summary-ad">
          <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide"><Megaphone className="w-4 h-4 text-[#CC5A3A]" /> Ad revenue</div>
          <div className="font-heading text-2xl text-[#1E3F33] mt-2">${adRevenue.toFixed(2)}</div>
        </div>
        <div className="card-soft" data-testid="admin-payments-summary-subscription">
          <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide"><Repeat className="w-4 h-4 text-[#2D6A4F]" /> Subscription revenue</div>
          <div className="font-heading text-2xl text-[#1E3F33] mt-2">${subRevenue.toFixed(2)}</div>
        </div>
        <div className="card-soft" data-testid="admin-payments-summary-active-subs">
          <div className="flex items-center gap-2 text-[#5C6B64] text-xs uppercase tracking-wide"><Users className="w-4 h-4 text-[#2D6A4F]" /> Active subscribers</div>
          <div className="font-heading text-2xl text-[#1E3F33] mt-2">{activeSubs === null ? "—" : activeSubs}</div>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <FilterPills options={TYPE_OPTIONS} value={type} onChange={setType} testidPrefix="admin-payments-type-filter" />
          <FilterPills options={STATUS_OPTIONS} value={status} onChange={setStatus} testidPrefix="admin-payments-status-filter" />
          <button
            data-testid="admin-payments-export-csv"
            onClick={exportCSV}
            disabled={!filtered.length}
            className="btn-secondary inline-flex items-center gap-2 text-sm py-1.5 px-3 disabled:opacity-50 ml-auto"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
        <DateRangeFilter onChange={(start, end) => setRange({ start, end })} defaultPreset="month" />
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : filtered.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]" data-testid="admin-payments-empty">
          {items.length === 0 ? "No payments yet." : "No payments match these filters."}
        </p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Item</th>
              <th className="px-4 py-2.5 font-medium">Payer</th>
              <th className="px-4 py-2.5 font-medium">Date</th>
              <th className="px-4 py-2.5 font-medium">Amount</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-payment-row-${t.id}`}>
                  <td className="px-4 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full ${typeBadge(t.type)}`} data-testid={`admin-payment-type-${t.id}`}>{typeLabel(t.type)}</span></td>
                  <td className="px-4 py-2.5 font-medium text-[#1E3F33]">{t.item || t.ad_title || "—"}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{t.payer || t.advertiser_email || "—"}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{fmt(t.created_at)}</td>
                  <td className="px-4 py-2.5 text-[#1E3F33]">${Number(t.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full ${badge(t.payment_status)}`} data-testid={`admin-payment-status-${t.id}`}>{label(t.payment_status)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
